/**
 * [OffersV3]
 *
 * This file contains the old mapping logic between the new Offers Model V3 and
 * the old daedalus backend augmentation format.
 *
 * Added as part of [this epic](https://app.shortcut.com/findhotel/epic/62938/offers-v3-model-search-clients-migration)
 * as a temporary solution while we migrate all usages to the new models.
 * Should be deleted after the epic is completed.
 */

import {
  HotelOfferEntity,
  HotelOfferEntity as SapiHotelOfferEntity,
  SearchResults
} from '@findhotel/sapi'
import {getMeta} from 'modules/meta/selectors'
import {getIsMultiRoomBundlesSearch} from 'modules/search/selectors'
import {AppThunk} from 'store'

import {
  MappingContext,
  OldHotelOfferEntity,
  sapiHotelOfferEntityToHotelOfferEntity as sapiHotelOfferEntityToHotelOfferEntityCore
} from '@vio/core/src/offer/business/offersMapping'
import {roomsToConfigurationObject} from '@vio/core/src/room/business/roomConfiguration'
import {BaseMappingContext} from '@vio/core/src/sapi/services/searchApi/utils'

import {getSearchParameters, State} from './selectors'
import {
  anchorOffersReceived,
  offersReceived,
  OffersReceivedPayload
} from './slice'

export type {OldHotelOfferEntity} from '@vio/core/src/offer/business/offersMapping'

/**
 * Extend core offer mapping with local modifications for client
 */
function sapiHotelOfferEntityToHotelOfferEntity(
  offerEntity: SapiHotelOfferEntity,
  context: MappingContext
): OldHotelOfferEntity | undefined {
  const mappedOfferEntity = sapiHotelOfferEntityToHotelOfferEntityCore(
    offerEntity,
    context
  )

  return {
    ...mappedOfferEntity,
    offers: mappedOfferEntity.offers
  }
}

/**
 * Base offer mapping options (enough for `core/searchApi`)
 */
export function getBaseMappingContextFromState(
  state: State
): BaseMappingContext {
  const {includeLocalTaxes, includeTaxes} = getMeta(state)
  const isMultiRoomBundlesSearch = getIsMultiRoomBundlesSearch(state)
  return {
    includeLocalTaxes,
    includeTaxes,
    includeRoomsInNightlyPrice: isMultiRoomBundlesSearch
  }
}

/**
 * Full offer mapping options including the searched itinerary
 */
export function getMappingContextFromState(state: State): MappingContext {
  const {checkIn, checkOut, rooms} = getSearchParameters(state)
  const {numberOfRooms} = roomsToConfigurationObject(rooms)
  return {
    ...getBaseMappingContextFromState(state),
    checkIn,
    checkOut,
    numberOfRooms
  }
}

/**
 * Map offer entity & fire anchorOffersReceived
 */
export const anchorOffersReceivedWithMapping =
  (sapiHotelOfferEntity: HotelOfferEntity): AppThunk =>
  (dispatch, getState) => {
    const state = getState()
    const context = getMappingContextFromState(state)
    const mapped = sapiHotelOfferEntityToHotelOfferEntity(
      sapiHotelOfferEntity,
      context
    )
    dispatch(anchorOffersReceived(mapped))
  }

interface OfferWithMappingPayload
  extends Omit<OffersReceivedPayload, 'hotelOfferEntities'> {
  hotelOfferEntities: SearchResults['hotelOfferEntities']
}

/**
 * Map offer entities and fire offersReceived
 */
export const offersReceivedWithMapping =
  (payload: OfferWithMappingPayload): AppThunk =>
  (dispatch, getState) => {
    const state = getState()
    if (!payload?.hotelOfferEntities)
      return dispatch(offersReceived({...payload, hotelOfferEntities: {}}))

    const context = getMappingContextFromState(state)

    const hotelOfferEntities = Object.fromEntries(
      Object.entries(payload.hotelOfferEntities).map(
        ([hotelId, sapiHotelOfferEntity]) => {
          return [
            hotelId,
            sapiHotelOfferEntityToHotelOfferEntity(
              sapiHotelOfferEntity,
              context
            )
          ]
        }
      )
    )

    dispatch(offersReceived({...payload, hotelOfferEntities}))
  }
